import type { ReactNode } from 'react';
import { Avatar, avatarHashIdToColor, getAvatarInitials } from '@clay-run/glaze/avatar';
import { Icon } from '@clay-run/glaze/icon';
import { FLOW_ICONS } from '../../icons';

/** What leads a row: a record's avatar, or an icon tile for an aggregate such as All or Contacts. */
export type NotesOptionMark =
  | { kind: 'account'; id: string }
  | { kind: 'person'; id: string }
  | { kind: 'group'; icon: 'people' | 'companies' | 'notes' };

/** The mark for a record: square for an account, round for anyone else (contact or teammate). */
export function recordMark(kind: string, id: string): NotesOptionMark {
  return kind === 'account' ? { kind: 'account', id } : { kind: 'person', id };
}

export interface NotesOptionRowProps {
  label: string;
  /** Muted second line (a title, say). */
  detail?: string | null;
  mark: NotesOptionMark;
  /** Right-aligned content, e.g. the note count. */
  trailing?: ReactNode;
}

/**
 * One row of a Notes picker, shared by "Posting to", the People filter and the trigger of each:
 * Glaze Avatar (square for an account, round for a person, colour hashed from the record id so a
 * record keeps its colour everywhere), the name, and optional detail and trailing content.
 */
export function NotesOptionRow({ label, detail, mark, trailing }: NotesOptionRowProps) {
  return (
    <span className="flex min-w-0 flex-1 items-center gap-2">
      <NotesOptionMarkTile label={label} mark={mark} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate">{label}</span>
        {detail ? <span className="truncate text-xs text-content-tertiary">{detail}</span> : null}
      </span>
      {trailing}
    </span>
  );
}

function NotesOptionMarkTile({ label, mark }: { label: string; mark: NotesOptionMark }) {
  if (mark.kind === 'group') {
    return (
      <span
        data-testid="notes-option-mark"
        data-shape="icon"
        className="flex size-6 shrink-0 items-center justify-center rounded-full bg-bg-secondary text-content-secondary"
      >
        <Icon type={FLOW_ICONS[mark.icon]} size="sm" aria-hidden />
      </span>
    );
  }
  const color = avatarHashIdToColor(mark.id);
  const shape = mark.kind === 'account' ? 'square' : 'circular';
  return (
    <span data-testid="notes-option-mark" data-shape={shape} data-color={color} className="flex shrink-0">
      <Avatar size="sm" shape={shape} color={color} fallbackString={getAvatarInitials(label.replace(/\s+/g, ' ').trim(), '?')} alt="" />
    </span>
  );
}
